// @ts-check
import useAssetDetails from 'modules/queries/asset/useDetails';
import useAcronisUsages from 'modules/queries/acronis/backup/useUsages';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import {
	getMonthlyBackupCost,
	getTotalBackupSize,
} from 'modules/backups/helpers';

/**
 * Custom hook for fetching and processing backup data
 * @param {string} uniqId - The unique ID of the asset
 * @returns {{
 *   isLoading: boolean,
 *   hasAcronis: boolean | undefined,
 *   acronisBackupsReady: boolean | undefined,
 *   totalSize: string | undefined,
 *   monthlyCost: number | undefined
 * }} Backup data
 */
const useBackups = (uniqId) => {
	// Get asset details
	const { data: assetDetailsData, isInitialLoading: isLoadingAssetDetails } =
		useAssetDetails(
			{
				uniq_id: uniqId,
				alsowith: hostingDetailsAlsowith,
			},
			{
				enabled: Boolean(uniqId),
			},
		);

	// Extract Acronis ID
	const acronisId = assetDetailsData?.acronisId;
	const acronisBackupsReady = assetDetailsData?.acronisStatus === 'Active';

	// Get Acronis usage data
	const { data: acronisUsages, isInitialLoading: isLoadingAcronisUsagesRaw } =
		useAcronisUsages(
			{
				uniq_id: acronisId ?? '',
			},
			{
				enabled: acronisBackupsReady,
			},
		);

	const isLoadingAcronisUsages =
		acronisBackupsReady && isLoadingAcronisUsagesRaw;

	// Get Acronis asset details
	const {
		data: acronisAssetDetails,
		isInitialLoading: isLoadingAcronisAssetDetails,
	} = useAssetDetails(
		{
			uniq_id: acronisId ?? '',
			alsowith: ['featureDetails', 'acronisBackingUp', 'product'],
		},
		{ enabled: Boolean(acronisId) },
	);

	const acronisProductCode = acronisAssetDetails?.product?.product_code;

	// Get Acronis product details for pricing
	const { data: acronisProductDetails } = useProductFlatDetails(
		{
			product_code: acronisProductCode ?? '',
		},
		{
			enabled: Boolean(acronisProductCode),
			staleTime: 10 * 60 * 1000, // unlikely to change, prevent unnecessary re-fetching
		},
	);

	// Calculate total size and monthly cost
	const totalSize = getTotalBackupSize(assetDetailsData, acronisUsages);
	const monthlyCost = getMonthlyBackupCost(
		assetDetailsData,
		acronisProductDetails,
		acronisAssetDetails,
	);

	return {
		isLoading:
			isLoadingAssetDetails ||
			isLoadingAcronisUsages ||
			isLoadingAcronisAssetDetails,
		hasAcronis: assetDetailsData?.hasAcronis,
		acronisBackupsReady,
		totalSize,
		monthlyCost,
	};
};

export default useBackups;
